
import React from 'react';
import { format } from 'date-fns';
import { Loader2, Sparkles } from 'lucide-react';
import { InventoryItem } from '@/types/inventory';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import HealthChart from './charts/HealthChart';
import CategoryChart from './charts/CategoryChart';
import TrendChart from './charts/TrendChart';
import { calculateStats, getItemStatus } from './exportUtils';

interface ReportPreviewProps {
  filteredItems: InventoryItem[];
  aiSummary: string | null;
  generatingAiSummary: boolean;
  onBack: () => void;
}

const ReportPreview: React.FC<ReportPreviewProps> = ({
  filteredItems,
  aiSummary,
  generatingAiSummary,
  onBack
}) => {
  const stats = calculateStats(filteredItems);

  return (
    <div className="grid gap-6 py-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-lg">Report Preview</h3>
        <Button 
          variant="outline" 
          size="sm"
          onClick={onBack}
        >
          Back to Settings
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <HealthChart data={stats} />
        <CategoryChart data={stats.categoryData} />
        <TrendChart data={stats.trendData} />
      </div>
      
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-sm font-medium mb-3">Selected Inventory Items</h3>
        <div className="max-h-[250px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500">
                    No items match your current filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.slice(0, 10).map(item => (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>
                      {item.has_expiry && item.expiry_date 
                        ? format(new Date(item.expiry_date), 'MMM d, yyyy')
                        : 'No Expiry'}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs",
                        getItemStatus(item) === 'Good' && "bg-green-100 text-green-800",
                        getItemStatus(item) === 'Expiring Soon' && "bg-yellow-100 text-yellow-800",
                        getItemStatus(item) === 'Expired' && "bg-red-100 text-red-800",
                        getItemStatus(item) === 'No Expiry' && "bg-gray-100 text-gray-800"
                      )}>
                        {getItemStatus(item)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {filteredItems.length > 10 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 italic">
                    + {filteredItems.length - 10} more items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      
      <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center mb-3">
          <Sparkles className="h-5 w-5 text-amber-500 mr-2" />
          <h3 className="text-sm font-medium">AI-Generated Insights</h3>
        </div>
        
        {generatingAiSummary ? (
          <div className="flex flex-col items-center justify-center p-8 space-y-3 bg-gray-50 rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <p className="text-gray-600">Generating AI insights from your inventory data...</p>
            <p className="text-xs text-gray-500">This may take a few moments</p>
          </div>
        ) : aiSummary ? (
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg">
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
              {aiSummary}
            </p>
          </div>
        ) : (
          <div className="p-6 text-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">No AI insights available</p>
          </div>
        )}
      </div>
      
      <p className="text-sm text-gray-500 italic">
        Note: This is a preview. The generated PDF report will include all selected items 
        and enhanced visualizations.
      </p>
    </div>
  );
};

export default ReportPreview;
